import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import { token } from "@atlaskit/tokens";

import {
    useUsageOutput,
    useAllowancesOutput,
    useAiCreditsExceeded
} from "@/components/usage-based-pricing-calculator/src/hooks";
import { CapacityCard } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

import { messages } from "./capacity-sidebar.messages";
import { FeedbackButtons } from "./feedback-buttons.tsx";
import { StatusMessage } from "./status-message";

const sidebarStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "rgba(23, 23, 23, 0.03)",
    padding: token("space.300"),
    borderRadius: "12px"
});

const titleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "20px",
    fontWeight: 700,
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.200")
});

const statusTextStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    color: wacToken("color.primary.black"),
    lineHeight: "1.5"
});

const cardsContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.150"),
    marginBlockEnd: token("space.250")
});

const disclaimerStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "12px",
    lineHeight: "1.5",

    color: wacToken("color.neutral.light700"),
    marginBlockStart: token("space.200")
});

export const CapacitySidebar = () => {
    const intl = useIntl();
    const usage = useUsageOutput();
    const allowances = useAllowancesOutput();
    const aiCreditsExceeded = useAiCreditsExceeded();

    const isDefault = !allowances.aiCredits;
    const isExceeds = aiCreditsExceeded;

    return (
        <div css={sidebarStyles} data-testid="capacity-sidebar">
            <Heading size="small" as="h3">
                <span css={titleStyles}>
                    <FormattedMessage {...messages.sidebarTitle} />
                </span>
            </Heading>

            {isDefault ? (
                <p css={statusTextStyles}>
                    <FormattedMessage {...messages.adjustInputsMessage} />
                </p>
            ) : (
                <StatusMessage exceeds={isExceeds} />
            )}

            <div css={cardsContainerStyles}>
                <CapacityCard
                    label={intl.formatMessage(messages.creditsLabel)}
                    value={Math.round(
                        usage.aiCredits ? usage.aiCredits : allowances.aiCredits
                    )}
                    maxValue={
                        usage.aiCredits
                            ? Math.round(allowances.aiCredits)
                            : undefined
                    }
                    status={
                        aiCreditsExceeded
                            ? "error"
                            : isDefault
                            ? "default"
                            : "success"
                    }
                    description={undefined}
                />
            </div>
            <p css={disclaimerStyles}>
                <FormattedMessage {...messages.sidebarDisclaimer} />
            </p>

            <FeedbackButtons />
        </div>
    );
};
